import axios from 'axios';
import { attachUserFacingMessage } from './userFacingError';

// `server/index.js` serves HTTP on 127.0.0.1:3001 by default. If the SPA is
// configured with https://localhost:3001 (or similar), the browser shows
// net::ERR_SSL_PROTOCOL_ERROR. Set REACT_APP_LOCAL_API_USE_HTTPS=1 only when
// the local API runs with LOCAL_DEV_HTTPS=1.
const allowLocalApiTls =
  String(process.env.REACT_APP_LOCAL_API_USE_HTTPS || '').trim() === '1';

function coerceLocalApiToHttp(url) {
  if (!url || allowLocalApiTls) return url;
  try {
    const u = new URL(String(url).trim());
    if (u.protocol !== 'https:') return String(url).trim();
    if (u.port !== '3001') return String(url).trim();
    if (u.hostname !== 'localhost' && u.hostname !== '127.0.0.1') return String(url).trim();
    return `http://${u.hostname}:3001`;
  } catch {
    return url;
  }
}

// Resolve API base URL dynamically for Cloudflare and other environments.
// Priority: window.__API_BASE_URL__ > REACT_APP_API_BASE_URL > local CRA proxy (same-origin) > same-origin ('')
const windowInjectedBase = coerceLocalApiToHttp(
  (typeof window !== 'undefined' && window.__API_BASE_URL__) ? String(window.__API_BASE_URL__) : '',
);
const envBase = coerceLocalApiToHttp(
  process.env.REACT_APP_API_BASE_URL ? String(process.env.REACT_APP_API_BASE_URL) : '',
);

const isLocalSpa =
  (typeof window !== 'undefined') &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
  String(window.location.port || '') === '3000';

const envLocalDevBase = process.env.REACT_APP_LOCAL_DEV_API_BASE_URL
  ? String(process.env.REACT_APP_LOCAL_DEV_API_BASE_URL)
  : '';
const localDevApiBase = coerceLocalApiToHttp(envLocalDevBase || 'http://127.0.0.1:3001');

function isLocalDevApiBaseUrl(baseUrl) {
  try {
    if (!baseUrl) return false;
    const u = new URL(String(baseUrl));
    const hostOk = u.hostname === '127.0.0.1' || u.hostname === 'localhost';
    const portOk = String(u.port || '') === '3001';
    return hostOk && portOk;
  } catch {
    return false;
  }
}

const looksLikeSameOriginBase = (u) => {
  try {
    if (!u) return false;
    const raw = String(u).trim();
    if (raw === '/' || raw === '.') return true;
    if (typeof window === 'undefined') return false;
    if (raw === window.location.origin) return true;
    return raw.includes('localhost:3000') || raw.includes('127.0.0.1:3000');
  } catch {
    return false;
  }
};

const resolvedBaseUrl = (() => {
  if (windowInjectedBase) return windowInjectedBase;
  if (envBase && !looksLikeSameOriginBase(envBase)) return envBase;
  if (isLocalSpa) return '';
  return '';
})();

try {
  if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.info('[api] baseURL', {
      resolvedBaseUrl,
      windowInjectedBase: windowInjectedBase || null,
      envBase: envBase || null,
      envLocalDevBase: envLocalDevBase || null,
      isLocalSpa,
      lpaLocalOverrideEnabled: isLocalSpa && String(process.env.REACT_APP_LPA_USE_LOCAL_BACKEND || '1').trim() !== '0',
      willLocalOverrideEnabled: isLocalSpa && String(process.env.REACT_APP_WILL_USE_LOCAL_BACKEND || '1').trim() !== '0',
    });
  }
} catch {}

const api = axios.create({ baseURL: resolvedBaseUrl });
api.defaults.withCredentials = true;

api.defaults.headers.post['Content-Type'] = 'application/json; charset=utf-8';
api.defaults.headers.put['Content-Type'] = 'application/json; charset=utf-8';
api.defaults.headers.patch['Content-Type'] = 'application/json; charset=utf-8';

// Restore bearer token from sessionStorage if a previous login used the
// bearer fallback (Aurora/KMS unreachable locally so no cookie session).
try {
  const stored = typeof window !== 'undefined' && sessionStorage.getItem('pv_bearer_token');
  if (stored) {
    api.defaults.headers.common['Authorization'] = `Bearer ${stored}`;
  }
} catch {}

/** Persist CSRF token from GET /auth/csrf (required when SPA and API are on different origins). */
export function storeCsrfFromResponse(res) {
  try {
    const token = res?.data?.csrfToken;
    if (token && typeof window !== 'undefined') {
      window.sessionStorage.setItem('pv_csrf_token', String(token));
    }
  } catch (_) {}
}

/** Fetch /auth/csrf and sync sessionStorage so mutating requests include X-CSRF-Token. */
export async function refreshCsrfToken() {
  const res = await api.get('/auth/csrf');
  storeCsrfFromResponse(res);
  return res;
}

/**
 * Read the CSRF token to attach to mutating requests.
 * Tries document.cookie first (works when API and SPA share the same eTLD+1 or
 * in local dev). Falls back to sessionStorage for cross-origin deployments where
 * the pv_csrf cookie belongs to the API domain and is not visible to the SPA's
 * document.cookie; call refreshCsrfToken() after login and on app bootstrap.
 */
function readCsrfCookie() {
  if (typeof document !== 'undefined') {
    try {
      const m = document.cookie.match(/(?:^|;\s*)pv_csrf=([^;]+)/);
      if (m) return decodeURIComponent(m[1]);
    } catch {}
  }
  try {
    const stored =
      typeof window !== 'undefined' &&
      window.sessionStorage.getItem('pv_csrf_token');
    if (stored) return stored;
  } catch {}
  return '';
}

api.interceptors.request.use((config) => {
  try {
    const m = String(config.method || 'get').toLowerCase();
    if (['post', 'put', 'patch', 'delete'].includes(m)) {
      const t = readCsrfCookie();
      if (t) {
        config.headers = config.headers || {};
        config.headers['X-CSRF-Token'] = t;
      }
    }
  } catch {}
  return config;
});

// One-shot retry when sessionStorage CSRF was stale vs the API cookie (common after refresh).
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const cfg = error?.config;
    const status = error?.response?.status;
    const msg = String(error?.response?.data?.error || '');
    if (
      !cfg
      || cfg.__pvCsrfRetried
      || status !== 403
      || !/invalid csrf token/i.test(msg)
    ) {
      return Promise.reject(attachUserFacingMessage(error));
    }
    const method = String(cfg.method || 'get').toLowerCase();
    if (!['post', 'put', 'patch', 'delete'].includes(method)) {
      return Promise.reject(error);
    }
    try {
      await refreshCsrfToken();
      cfg.__pvCsrfRetried = true;
      return api.request(cfg);
    } catch {
      return Promise.reject(attachUserFacingMessage(error));
    }
  },
);

// Enrich all API failures with user-facing copy (runs before handlers that read err.userFacingMessage).
api.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(attachUserFacingMessage(error)),
);

/**
 * Returns true when an API call failed due to missing/invalid auth.
 * We treat both 401 and 403 as "auth problems" from the SPA's perspective.
 */
export function isAuthError(err) {
  const status = err?.response?.status;
  return status === 401 || status === 403;
}

/**
 * Clear any legacy client-side session snapshot (HttpOnly cookies are cleared via POST /auth/logout).
 */
export function clearStoredSession() {
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem('currentUser');
      window.sessionStorage.removeItem('pv_bearer_token');
      // Also clear the stored CSRF token so a stale value from a previous session
      // cannot persist into a new login attempt.
      window.sessionStorage.removeItem('pv_csrf_token');
      delete api.defaults.headers.common['Authorization'];
    }
  } catch {}
}

/** Redirect to the in-app login screen (Hosted UI + PKCE). */
export function redirectToLogin() {
  try {
    if (typeof window !== 'undefined') window.location.assign('/login');
  } catch {}
}

export { getUserFacingErrorMessage } from './userFacingError';
export default api;

api.interceptors.request.use((config) => {
  try {
    const lpaLocalOverrideEnabled =
      (typeof window !== 'undefined') &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
      String(window.location.port || '') === '3000' &&
      String(process.env.REACT_APP_LPA_USE_LOCAL_BACKEND || '1').trim() !== '0';

    const willLocalOverrideEnabled =
      (typeof window !== 'undefined') &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
      String(window.location.port || '') === '3000' &&
      String(process.env.REACT_APP_WILL_USE_LOCAL_BACKEND || '1').trim() !== '0';

    if (lpaLocalOverrideEnabled) {
      const url = String(config.url || '');
      if (url.startsWith('/lpa/')) {
        const base = String(config.baseURL || '');
        if (base && !isLocalDevApiBaseUrl(base)) {
          config.baseURL = localDevApiBase;
        }
      }
    }

    if (willLocalOverrideEnabled) {
      const url = String(config.url || '');
      if (url.startsWith('/will/')) {
        const base = String(config.baseURL || '');
        if (base && !isLocalDevApiBaseUrl(base)) {
          config.baseURL = localDevApiBase;
        }
      }
    }
  } catch {}
  return config;
});
